import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCanWrite } from '@/shared/hooks/useCanWrite';
import { inDestination, leafActive, leafHref, sectionsFor } from './nav-model';

/**
 * The pages of the current menu folder as tabs above the page (UI v2):
 * Posts shows All posts · Approvals · Social feeds, Calendar shows
 * Calendar · Next week, and so on. The pages themselves are unchanged —
 * each keeps its own route, so links and bookmarks keep working — the tabs
 * only make the siblings one click away, the way the menu folder does.
 */
export function SectionTabs() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { role } = useCanWrite();
  const folder = sectionsFor(role)
    .flatMap((s) => s.entries)
    .find((e) => e.children?.some((c) => inDestination(pathname, c.path)));
  const tabs = folder?.children;
  if (!folder || !tabs || tabs.length < 2) return null;
  return (
    <nav aria-label={t(folder.labelKey)} className="mb-4 -mt-1 border-b border-[var(--line)] overflow-x-auto no-scrollbar" data-testid="section-tabs">
      <ul className="flex gap-6 m-0 p-0 list-none min-w-max">
        {tabs.map((tab) => (
          <li key={tab.key}>
            <NavLink
              to={leafHref(tab)}
              end
              className={({ isActive: routeActive }) =>
                [
                  'relative inline-flex items-center h-10 pointer-coarse:h-11 text-ui no-underline whitespace-nowrap',
                  leafActive(tab, routeActive, search)
                    ? 'text-[var(--ink)] font-semibold after:absolute after:inset-x-0 after:-bottom-px after:h-[2px] after:rounded-full after:bg-[var(--color-accent)]'
                    : 'text-[var(--ink-3)] font-medium hover:text-[var(--ink)]',
                ].join(' ')
              }
            >
              {t(tab.labelKey)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
